import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { BRAND_GREEN_PRIMARY } from 'resources/constants/colors';
import useWellness from 'state/wellness';

// ---------------------------------------------------------------------------
// Animations
// ---------------------------------------------------------------------------

const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
`;

// ---------------------------------------------------------------------------
// Prompts — detonadores de escritura contextuales según el estado de ánimo
// ---------------------------------------------------------------------------

const PROMPTS_DEFAULT = [
    'Saca lo que traes en la cabeza…',
    '¿Qué te preocupa hoy?',
    'Escribe 3 cosas buenas de hoy.',
    '¿Qué necesitas escuchar ahora mismo?',
    '¿Qué fue lo más difícil de hoy?',
    '¿De qué te sientes orgulloso/a hoy?',
];

const PROMPTS_BY_MOOD = {
    1: [ // Muy mal
        '¿Qué necesitas en este momento?',
        'No estás solo/a. ¿Qué te gustaría decirte a ti mismo/a?',
        '¿Hay algo que te dé un poquito de calma?',
        'Escribe lo que sientes, sin filtro. Este espacio es tuyo.',
        '¿Qué te haría sentir un poco mejor ahora?',
    ],
    2: [ // Mal
        '¿Qué te está pesando hoy?',
        '¿Hay algo que quieras soltar aquí?',
        'Si pudieras cambiar algo de este día, ¿qué sería?',
        '¿Qué te ha costado más trabajo hoy?',
        '¿Qué le dirías a un amigo/a que se sintiera así?',
    ],
    3: [ // Regular
        '¿Hubo algún momento bueno hoy, aunque pequeño?',
        '¿Qué harías si tuvieras una hora libre ahora?',
        '¿Qué es algo que hiciste bien hoy?',
        'Los días neutros también cuentan. ¿Qué aprendiste?',
        '¿Hay algo pendiente que te gustaría resolver?',
    ],
    4: [ // Bien
        '¡Qué bueno! ¿Qué contribuyó a que te sientas así?',
        '¿Qué momento de hoy quieres recordar?',
        'Anota algo que te hizo sonreír hoy.',
        '¿Qué te gustaría repetir mañana?',
        '¿Hay alguien a quien quieras agradecer hoy?',
    ],
    5: [ // Muy bien
        '¡Celebra! ¿Qué te tiene tan bien?',
        '¿Qué logro quieres recordar de hoy?',
        'Escribe 3 razones por las que hoy fue un buen día.',
        '¿Qué te inspira cuando te sientes así?',
        '¿Cómo puedes llevar esta energía a mañana?',
    ],
};

const getPrompt = (moodValue) => {
    const dayOfYear = Math.floor(
        (Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 86_400_000
    );
    const prompts = (moodValue && PROMPTS_BY_MOOD[moodValue]) || PROMPTS_DEFAULT;
    return prompts[dayOfYear % prompts.length];
};

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const Card = styled.div`
    background: #fff;
    border-radius: 20px;
    padding: 18px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
    animation: ${fadeUp} 0.4s 0.3s ease both;
`;

const CardHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
`;

const CardTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const CardTitleText = styled.p`
    font-size: 14px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0;
`;

const PrivacyBadge = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: #43a047;
    font-weight: 600;
`;

const TextArea = styled.textarea`
    width: 100%;
    min-height: ${({ $expanded }) => $expanded ? '120px' : '56px'};
    background: #f8faf2;
    border: 1.5px solid ${({ $focused }) => $focused ? BRAND_GREEN_PRIMARY : '#e8ecd0'};
    border-radius: 14px;
    padding: 12px 14px;
    font-size: 14px;
    font-family: 'Open Sans', sans-serif;
    color: #333;
    resize: none;
    outline: none;
    line-height: 1.6;
    box-sizing: border-box;
    transition: border-color 0.2s ease, min-height 0.3s ease;

    &::placeholder {
        color: #b0b8a0;
    }
`;

const Footer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 10px;
`;

const CharCount = styled.span`
    font-size: 11px;
    color: ${({ $near }) => $near ? '#ef8c35' : '#bbb'};
`;

const SaveBtn = styled.button`
    background: ${BRAND_GREEN_PRIMARY};
    color: #fff;
    border: none;
    border-radius: 20px;
    padding: 8px 18px;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    opacity: ${({ disabled }) => disabled ? 0.4 : 1};
    -webkit-tap-highlight-color: transparent;
    transition: opacity 0.15s;

    &:active:not(:disabled) {
        opacity: 0.8;
    }
`;

const SuccessMsg = styled.div`
    text-align: center;
    padding: 10px 0 4px;
    animation: ${fadeUp} 0.3s ease;
`;

const SuccessEmoji = styled.p`
    font-size: 28px;
    margin: 0 0 4px;
`;

const SuccessText = styled.p`
    font-size: 13px;
    color: #43a047;
    font-weight: 600;
    margin: 0;
`;

const HistoryToggle = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    color: ${BRAND_GREEN_PRIMARY};
    padding: 8px 0 0;
    display: flex;
    align-items: center;
    gap: 4px;
    -webkit-tap-highlight-color: transparent;

    &:active {
        opacity: 0.7;
    }
`;

const HistoryList = styled.div`
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    animation: ${fadeUp} 0.3s ease;
`;

const EntryCard = styled.div`
    background: #f8faf2;
    border-radius: 12px;
    padding: 12px 14px;
`;

const EntryDate = styled.p`
    font-size: 11px;
    color: #999;
    margin: 0 0 4px;
    font-weight: 600;
`;

const EntryPrompt = styled.p`
    font-size: 11px;
    color: #b0b8a0;
    margin: 0 0 4px;
    font-style: italic;
`;

const EntryContent = styled.p`
    font-size: 13px;
    color: #333;
    margin: 0;
    line-height: 1.5;
    white-space: pre-wrap;
`;

const EntryMoodBadge = styled.span`
    font-size: 14px;
    margin-left: 6px;
`;

const ExportBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: 8px;
`;

const ExportBtn = styled.button`
    background: none;
    border: 1px solid #e0e0e0;
    border-radius: 16px;
    padding: 5px 14px;
    font-size: 11px;
    font-weight: 600;
    color: #666;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 4px;
    -webkit-tap-highlight-color: transparent;
    transition: all 0.15s;

    &:active {
        background: #f5f5f5;
    }
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const MAX_CHARS = 500;

const MOOD_EMOJI = { 1: '😣', 2: '😟', 3: '😐', 4: '🙂', 5: '😊' };

/**
 * Genera un PDF descargable a partir de las entradas del journal.
 * Usa la API nativa del navegador (canvas → blob) sin dependencias externas.
 * Cumple LFPDPPP Art. 9: el PDF se genera 100% client-side, nunca pasa por servidor.
 */
const exportJournalPDF = (entries, formatDateFn) => {
    const content = [
        'MI ESPACIO SEGURO — Diario Personal',
        `Generado: ${new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}`,
        'Este documento es privado y confidencial.',
        '',
        '─'.repeat(50),
        '',
    ];

    entries.forEach((entry) => {
        const emoji = entry.moodValue ? ` ${MOOD_EMOJI[entry.moodValue] || ''}` : '';
        content.push(`${formatDateFn(entry.date)}${emoji}`);
        if (entry.prompt) content.push(`  "${entry.prompt}"`);
        content.push('');
        content.push(`  ${entry.content}`);
        content.push('');
        content.push('─'.repeat(50));
        content.push('');
    });

    content.push('');
    content.push('Terappy® — Tu bienestar es tu derecho.');
    content.push('Documento generado localmente. Ningún dato fue transmitido.');

    const blob = new Blob([content.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mi-diario-terappy-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

const JournalEntry = () => {
    const [wellness, wellnessDispatcher] = useWellness();
    const [text, setText] = useState('');
    const [focused, setFocused] = useState(false);
    const [saved, setSaved] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const moodValue = wellness.todayMood?.value || null;
    const prompt = getPrompt(moodValue);
    const isSaving = wellness.fetching.saveJournal.state;
    const journals = wellness.journals || [];

    // Fetch journal history on mount
    useEffect(() => {
        wellnessDispatcher.fetchJournalStart(30);
    }, []);

    // Refresh after saving
    useEffect(() => {
        if (saved) {
            wellnessDispatcher.fetchJournalStart(30);
        }
    }, [saved]);

    const handleSave = () => {
        if (!text.trim() || isSaving) return;
        const payload = { content: text.trim(), promptUsed: prompt };
        // Link to today's mood if available
        if (wellness.todayMood?.id) {
            payload.moodCheckinId = wellness.todayMood.id;
        }
        wellnessDispatcher.saveJournalStart(payload);
        setSaved(true);
        setText('');
        setTimeout(() => setSaved(false), 3000);
    };

    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString('es-MX', {
            weekday: 'short', day: 'numeric', month: 'short',
        });
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle>
                    <span style={{ fontSize: 18 }}>📔</span>
                    <CardTitleText>Mi espacio seguro</CardTitleText>
                </CardTitle>
                <PrivacyBadge>
                    <span>🔒</span>
                    Solo tú
                </PrivacyBadge>
            </CardHeader>

            {saved ? (
                <SuccessMsg>
                    <SuccessEmoji>🌱</SuccessEmoji>
                    <SuccessText>Guardado. Aquí solo estás tú.</SuccessText>
                </SuccessMsg>
            ) : (
                <>
                    <TextArea
                        placeholder={prompt}
                        value={text}
                        $focused={focused}
                        $expanded={focused || text.length > 0}
                        maxLength={MAX_CHARS}
                        onFocus={() => setFocused(true)}
                        onBlur={() => setFocused(false)}
                        onChange={(e) => setText(e.target.value)}
                    />
                    <Footer>
                        <CharCount $near={text.length > MAX_CHARS * 0.8}>
                            {text.length}/{MAX_CHARS}
                        </CharCount>
                        <SaveBtn
                            disabled={!text.trim()}
                            onClick={handleSave}
                        >
                            Guardar
                        </SaveBtn>
                    </Footer>
                </>
            )}

            {/* Journal History */}
            {journals.length > 0 && (
                <>
                    <HistoryToggle onClick={() => setShowHistory(!showHistory)}>
                        {showHistory ? '▾' : '▸'} Mis entradas anteriores ({journals.length})
                    </HistoryToggle>

                    {showHistory && (
                        <>
                            <ExportBar>
                                <ExportBtn onClick={() => exportJournalPDF(journals, formatDate)}>
                                    📄 Descargar mi diario
                                </ExportBtn>
                            </ExportBar>
                            <HistoryList>
                                {journals.map((entry) => (
                                    <EntryCard key={entry.id}>
                                        <EntryDate>
                                            {formatDate(entry.date)}
                                            {entry.moodValue && (
                                                <EntryMoodBadge>
                                                    {MOOD_EMOJI[entry.moodValue]}
                                                </EntryMoodBadge>
                                            )}
                                        </EntryDate>
                                        {entry.prompt && (
                                            <EntryPrompt>"{entry.prompt}"</EntryPrompt>
                                        )}
                                        <EntryContent>{entry.content}</EntryContent>
                                    </EntryCard>
                                ))}
                            </HistoryList>
                        </>
                    )}
                </>
            )}
        </Card>
    );
};

export default JournalEntry;
